// Global click-tracker for any link whose destination contains `wa.me`.
//
// Mounted once at the app root (App.js). Listens at the document level in capture phase
// so it catches clicks even on Links inside modals, popovers and dynamically-rendered
// children. Fires the following analytics events on every WhatsApp click:
//
//   1. gtag('event', 'whatsapp_click', { … })  → visible in GA4 (G-VN6KJ01LY4)
//   2. gtag('event', 'generate_lead', { … })   → existing lead conversion event,
//                                                 so any GA4 "conversion" marker
//                                                 on generate_lead also fires here
//   3. gtag('event', 'conversion', { send_to: GOOGLE_ADS_CONVERSION_LABEL, … })
//                                              → only when a Google Ads Conversion
//                                                 Action label is configured
//   4. fbq('track', 'Lead', { … })             → Meta Pixel
//
// Both the Google Tag container (GT-KT9VXFFH) and GA4 (G-VN6KJ01LY4) are already
// loaded via gtag.js in /public/index.html — the same dataLayer routes events to
// all configured destinations, so a single gtag() call here populates every product.

import { useEffect } from "react";
import { trackEvent, trackLead } from "@/lib/analytics";

// Extract a friendly source label from the closest element ancestry. Helps us see
// in GA4 which CTA the user actually clicked (hero button, package card, footer link…).
function inferSource(target) {
  let el = target;
  while (el && el.nodeType === 1 && el !== document.body) {
    const tid = el.getAttribute && el.getAttribute("data-testid");
    if (tid) return tid;
    el = el.parentNode;
  }
  return "whatsapp_link";
}

// Find the closest anchor tag that initiated the navigation — handles clicks on
// <a><img/></a>, <a><span>…</span></a> etc.
function closestWhatsAppAnchor(target) {
  let el = target;
  while (el && el.nodeType === 1 && el !== document.body) {
    if (el.tagName === "A" && typeof el.href === "string") {
      const href = el.href.toLowerCase();
      if (href.includes("wa.me") || href.includes("api.whatsapp.com")) return el;
    }
    el = el.parentNode;
  }
  return null;
}

export default function WhatsAppClickTracker() {
  useEffect(() => {
    const onClick = (e) => {
      try {
        const anchor = closestWhatsAppAnchor(e.target);
        if (!anchor) return;
        const url = anchor.href || "";
        const source = inferSource(anchor);
        // Pull destination if the WA href already encodes a lead summary.
        let destination = null;
        try {
          const txt = new URL(url).searchParams.get("text") || "";
          const m = txt.match(/Destination:\s*([^\n]+)/i);
          if (m) destination = m[1].trim();
        } catch { /* ignore parse errors */ }

        // 1) Generic GA4 click event — surfaces in Realtime → "whatsapp_click".
        trackEvent("whatsapp_click", {
          link_url: url,
          link_domain: "wa.me",
          link_classes: anchor.className || undefined,
          link_id: anchor.id || undefined,
          source,
          destination: destination || undefined,
        });

        // 2) Lead conversion — fires generate_lead (GA4 + Meta Pixel) AND, when the
        //    Ads conversion label is set in analytics.js, the Google Ads conversion.
        trackLead({
          source: `wa_click_${source}`,
          destination,
          extra: { link_url: url },
        });
      } catch (err) {
        // never throw out of an analytics handler
        console.warn("WA click tracker failed", err);
      }
    };

    // Capture phase so we run BEFORE the browser navigates away from the page
    // (target='_blank' makes this fairly safe anyway, but capture phase + sync
    //  gtag/fbq calls guarantee the beacon is fired).
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null; // pure side-effect component
}
